import React, { useMemo } from 'react';
import { HourlyStat } from '../../types';
import './StatsChart.scss';

interface StatsChartProps {
  data: HourlyStat[];
  title?: string;
  color?: string;
}

const StatsChart: React.FC<StatsChartProps> = ({ data, title, color = 'var(--bs-primary)' }) => {
  const maxCount = useMemo(() => {
    if (!data || data.length === 0) return 1;
    return Math.max(...data.map((d) => d.count || 0), 1);
  }, [data]);

  // Format hour label to show only first 2 characters (e.g., "09" instead of "09:00")
  const formatHourLabel = (hour: string): string => {
    if (!hour) return '';
    // Extract first 2 characters if it's in format like "09:00" or "9:00"
    const match = hour.match(/^(\d{1,2})/);
    return match ? match[1].padStart(2, '0') : hour.substring(0, 2);
  };

  if (!data || data.length === 0) {
    return (
      <div className="stats-chart">
        <div className="chart-empty">
          <p className="text-muted mb-0">No data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="stats-chart">
      {title && <h6 className="chart-title">{title}</h6>}
      <div className="hourly-chart">
        {data.map((item, index) => {
          const count = item.count || 0;
          // Calculate height percentage, but ensure minimum height for visibility
          const heightPercent = maxCount > 0 ? (count / maxCount) * 100 : 0;
          // Use a minimum height in pixels (3px) or percentage, whichever is larger
          const minHeightPx = 3;
          const calculatedHeight = count > 0 ? Math.max(heightPercent, (minHeightPx / 120) * 100) : minHeightPx;
          
          return (
            <div key={index} className="hourly-bar-container">
              <div
                className="hourly-bar"
                style={{
                  height: count > 0 ? `${calculatedHeight}%` : `${minHeightPx}px`,
                  backgroundColor: color,
                }}
                title={`${item.hour}: ${count} scan${count !== 1 ? 's' : ''}`}
              />
              <div className="hourly-label">{formatHourLabel(item.hour)}</div>
            </div>
          );
        })}
      </div>
      <p className="text-muted text-center mt-2 mb-0">
        <small>Hover over bars to see exact counts</small>
      </p>
    </div>
  );
};

export default StatsChart;

